import { z } from 'zod';
import { uuidSchema, shortString, mediumString, positiveInt, nonNegativeInt, } from './validation.js';
import { purchaseOrderKindSchema } from './jit-purchase-orders.js';
/**
 * Purchase Order Schemas
 *
 * Shared validation schemas for purchase order operations.
 * Handles the workflow:
 * Draft -> Submitted -> In Production -> Ready -> Partially Received -> Received
 */
// BLU-377: 'paid' moved to a separate payment_status column.
// Fulfillment status is back to 8 values.
const purchaseOrderStatusSchema = z.enum([
    'draft',
    'submitted',
    'in_production',
    'ready',
    'in_transit',
    'partially_received',
    'received',
    'cancelled',
]);
// BLU-377: payment_status is independent of fulfillment status.
// A PO can be submitted+unpaid, in_transit+paid, received+unpaid, etc.
const paymentStatusSchema = z.enum(['unpaid', 'paid']);
const purchaseOrderSortFieldSchema = z.enum([
    'po_number',
    'supplier_name',
    'status',
    'submitted_date',
    'created_at',
    'total_cost',
]);
/**
 * Order type discriminator
 */
export const orderTypeSchema = z.enum(['purchase', 'transfer']);
/**
 * A purchase-order line item sourced from a product_dev record rather than
 * the SKU catalog ("Product Dev → Purchase Order"). `sku_id` stays null on
 * the row — `product_dev_id` + `item_name` carry identity instead.
 */
export const purchaseOrderDevItemSchema = z.object({
    productDevId: uuidSchema,
    itemName: z.string().trim().min(1).max(300),
    orderedQuantity: positiveInt.max(1_000_000),
    unitCost: z.number().min(0).max(10_000_000),
    unitCostCurrency: z.string().length(3).toUpperCase(),
});
/**
 * Create a new purchase order or transfer order
 */
export const createPurchaseOrderSchema = z.object({
    locationId: uuidSchema,
    orderType: orderTypeSchema.optional().default('purchase'),
    sourceLocationId: uuidSchema.optional(),
    supplierId: uuidSchema.optional(),
    supplierName: shortString.optional().default(''),
    title: shortString.optional(),
    description: mediumString.optional(),
    etaDate: z.string().date().nullable().optional(),
    price: z.number().positive('Price must be positive').optional(),
    status: z.enum(['draft', 'submitted']).optional(),
    currency: z.string().length(3).toUpperCase().optional(),
    exchangeRate: z.number().positive().optional(),
    items: z.array(z.object({
        skuId: uuidSchema,
        orderedQuantity: positiveInt,
    })),
    devItems: z.array(purchaseOrderDevItemSchema).optional(),
}).refine((data) => data.items.length + (data.devItems?.length ?? 0) >= 1, {
    message: 'At least one item required',
    path: ['items'],
});
/**
 * Update purchase order items (re-edit functionality)
 */
export const updatePurchaseOrderItemsSchema = z.object({
    orderId: uuidSchema,
    items: z.array(z.object({
        itemId: uuidSchema,
        orderedQuantity: positiveInt,
    })).min(1, 'At least one item required'),
});
/**
 * Receive purchase order (mark items as received)
 */
export const receivePurchaseOrderSchema = z.object({
    orderId: uuidSchema,
    items: z.array(z.object({
        itemId: uuidSchema,
        receivedQuantity: nonNegativeInt,
    })).min(1, 'At least one item required'),
    notes: mediumString.optional(),
});
/**
 * Transit purchase order (mark as in transit)
 */
export const transitPurchaseOrderSchema = z.object({
    orderId: uuidSchema,
    trackingNumber: shortString.optional(),
    estimatedArrival: z.string().datetime().optional(),
});
/**
 * Cancel purchase order
 */
export const cancelPurchaseOrderSchema = z.object({
    orderId: uuidSchema,
    reason: mediumString.min(1, 'Cancellation reason is required'),
});
/**
 * Get purchase orders with filters
 */
export const getPurchaseOrdersSchema = z.object({
    locationId: uuidSchema,
    status: purchaseOrderStatusSchema.optional(),
    limit: positiveInt.max(100, 'Limit cannot exceed 100').default(50).optional(),
    offset: nonNegativeInt.default(0).optional(),
});
/**
 * Update purchase order status
 */
export const updateOrderStatusSchema = z.object({
    orderId: uuidSchema,
    locationId: uuidSchema,
    status: purchaseOrderStatusSchema,
});
export const ORDER_DATE_FIELDS = [
    { input: 'supplierConfirmedAt', column: 'supplier_confirmed_at', kind: 'instant', purchaseOnly: true },
    { input: 'etaDate', column: 'eta_date', kind: 'calendar', purchaseOnly: true },
    { input: 'shippedDate', column: 'shipped_date', kind: 'instant', purchaseOnly: false },
    { input: 'arrivalEtaDate', column: 'arrival_eta_date', kind: 'calendar', purchaseOnly: false },
];
export const ORDER_DATE_FIELD_KIND = Object.fromEntries(ORDER_DATE_FIELDS.map((f) => [f.input, f.kind]));
const DATE_VALUE = {
    calendar: z.string().date().nullable().optional(), // DATE column: exactly YYYY-MM-DD, a real day
    instant: z.string().datetime({ offset: true }).nullable().optional(), // TIMESTAMPTZ: ISO instant, Z or offset
};
export const updateOrderDatesSchema = z
    .object({
    orderId: z.string().uuid(),
    locationId: z.string().uuid(), // destination location (requireLocationAccess)
    supplierConfirmedAt: DATE_VALUE[ORDER_DATE_FIELD_KIND.supplierConfirmedAt],
    etaDate: DATE_VALUE[ORDER_DATE_FIELD_KIND.etaDate],
    shippedDate: DATE_VALUE[ORDER_DATE_FIELD_KIND.shippedDate],
    arrivalEtaDate: DATE_VALUE[ORDER_DATE_FIELD_KIND.arrivalEtaDate],
})
    .refine((v) => ORDER_DATE_FIELDS.some((f) => v[f.input] !== undefined), {
    message: 'No date provided',
});
export const getOrderDateHistorySchema = z.object({ orderId: z.string().uuid() });
/**
 * Complete a partially-shipped purchase order (BLU-572). Short-closes a
 * `partially_received` order straight to `received` so its undershipped
 * (ordered - received) leftover stops counting as incoming stock. Does NOT
 * touch line items / received_quantity and does NOT move any stock.
 */
export const completePurchaseOrderSchema = z.object({
    orderId: uuidSchema,
    locationId: uuidSchema,
});
/**
 * Get purchase orders, optionally filtered by location
 */
export const getOrdersSchema = z.object({
    locationId: uuidSchema.optional(),
    orderType: orderTypeSchema.optional(),
    status: purchaseOrderStatusSchema.optional(),
    statuses: z.array(purchaseOrderStatusSchema).min(1).max(8).optional(),
    // Exact FK filter (purchase_orders.supplier_id). Legacy rows created before
    // the FK existed carry only supplier_name text — those won't match; the
    // searchTerm path (supplier_name ilike) still finds them.
    supplierId: uuidSchema.optional(),
    // BLU-1268: standard, just-in-time or order-and-wait purchase orders (To buy's
    // "Buy today" reads kind just_in_time). Applied in the query, before the page is cut.
    kind: purchaseOrderKindSchema.optional(),
    // BLU-377: filter by payment_status independently from fulfillment status.
    paymentStatus: paymentStatusSchema.optional(),
    searchTerm: mediumString.optional(),
    sortBy: purchaseOrderSortFieldSchema.optional(),
    sortOrder: z.enum(['asc', 'desc']).optional(),
    limit: positiveInt.max(200, 'Limit cannot exceed 200').optional(),
    offset: nonNegativeInt.optional(),
});
/**
 * Get purchase orders for a supplier (across locations)
 */
export const getOrdersBySupplierSchema = z.object({
    supplierId: uuidSchema,
});
/**
 * Process an order item (add stock to inventory)
 */
export const processItemSchema = z.object({
    itemId: uuidSchema,
    action: z.enum(['add_stock', 'edited', 'not_received']),
    receivedQuantity: nonNegativeInt,
    notes: mediumString.optional(),
    processedBy: shortString.min(1, 'Processor ID is required'),
});
/**
 * BLU-516: add a catalog SKU that arrived from the supplier but wasn't on
 * the original PO — discovered at receiving time. Only accepted while the
 * order is actually in flight (submitted/in_transit/partially_received);
 * the service enforces that, not this schema.
 */
export const addSurpriseItemSchema = z.object({
    poId: uuidSchema,
    skuId: uuidSchema,
    quantity: positiveInt.max(1_000_000),
});
/**
 * Re-edit an order item (adjust stock difference)
 */
export const reEditOrderItemSchema = z.object({
    itemId: uuidSchema,
    newReceivedQuantity: nonNegativeInt,
    notes: mediumString.optional(),
    processedBy: shortString.min(1, 'Processor ID is required'),
});
/**
 * Delete a draft order
 */
export const deleteDraftSchema = z.object({
    orderId: uuidSchema,
    locationId: uuidSchema,
});
/**
 * Submit a draft order (convert draft to submitted)
 */
export const submitDraftSchema = z.object({
    orderId: uuidSchema,
    locationId: uuidSchema,
    supplierId: uuidSchema.optional(),
    supplierName: shortString.optional().default(''),
});
/**
 * Update a draft order
 */
export const updateDraftSchema = z.object({
    orderId: uuidSchema,
    locationId: uuidSchema,
    supplierId: uuidSchema.nullable().optional(),
    supplierName: shortString.nullable().optional(),
    title: shortString.nullable().optional(),
    description: mediumString.nullable().optional(),
    etaDate: z.string().date().nullable().optional(),
    price: z.number().nullable().optional(),
    currency: z.string().length(3).toUpperCase().nullable().optional(),
    exchangeRate: z.number().positive().nullable().optional(),
    orderQuantities: z.record(z.string().uuid(), z.number().nonnegative()).optional(),
    // BLU-571: per-line unit-cost overrides, keyed by skuId (parallel to
    // orderQuantities). Set/change only — the edit form sends only lines whose
    // cost actually changed; clearing an override back to catalog stays on the
    // OrderDetail PriceOverrideCell path (which sends `overriddenUnitPrice: null`).
    // Bounds mirror overridePurchaseOrderItemPriceSchema below.
    unitCosts: z.record(z.string().uuid(), z.number().positive().max(99999999.99).finite()).optional(),
});
/**
 * Update shipped quantities (packing list data entry)
 */
export const updateShippedQuantitiesSchema = z.object({
    orderId: uuidSchema,
    items: z.array(z.object({
        itemId: uuidSchema,
        shippedQuantity: nonNegativeInt,
    })).min(1, 'At least one item required'),
});
/**
 * Ship a transfer with a quantity per SKU (BLU-535 section D).
 *
 * Every stockable line on the transfer must appear exactly once, with a
 * quantity between 0 and that line's ordered quantity. The service validates
 * the payload against the order before any stock moves, and ship_transfer_items
 * re-locks and re-checks each row rather than trusting the caller.
 */
export const shipTransferSchema = z.object({
    orderId: uuidSchema,
    locationId: uuidSchema,
    // Deliberately unbounded. Order creation puts no ceiling on line count and
    // ship_transfer_items refuses any payload that does not cover EVERY stockable
    // line, so a cap here would make a transfer larger than the cap impossible to
    // ship at all.
    items: z.array(z.object({
        itemId: uuidSchema,
        shippedQuantity: nonNegativeInt,
    })).min(1, 'At least one item required'),
});
/**
 * Confirm the ordered units that never left the source on a shipped transfer.
 * `itemIds` absent means every line that still has an unconfirmed shortfall.
 */
export const confirmTransferUnshippedSchema = z.object({
    orderId: uuidSchema,
    locationId: uuidSchema,
    // Unbounded for the same reason as shipTransfer's items: the list is a subset
    // of one transfer's lines, and an absent list already means every eligible
    // line, so a cap could only ever block a legitimate large transfer.
    itemIds: z.array(uuidSchema).optional(),
});
/**
 * Resolve shipped units that never reached the destination on a stamped
 * transfer: `returned` puts them back on hand at the source, `scrapped` records
 * them as lost in transit (audit only, no stock move).
 */
export const UNRECEIVED_RESOLUTION_VALUES = ['returned', 'scrapped'];
export const unreceivedResolutionSchema = z.enum(UNRECEIVED_RESOLUTION_VALUES);
export const resolveTransferUnreceivedSchema = z.object({
    orderId: uuidSchema,
    locationId: uuidSchema, // destination, same as confirmTransferUnshipped
    itemIds: z.array(uuidSchema).min(1).max(500),
    resolution: unreceivedResolutionSchema,
});
/**
 * Additional cost type enum for purchase orders
 */
export const additionalCostTypeSchema = z.enum([
    'freight',
    'customs',
    'sampling',
    'mould',
    'other',
]);
/**
 * Upsert an additional cost line on a purchase order
 */
export const upsertAdditionalCostSchema = z.object({
    orderId: uuidSchema,
    costId: uuidSchema.optional(),
    costType: additionalCostTypeSchema,
    label: z.string().max(200).nullable().optional(),
    amount: z.number().min(-9_999_999, 'Amount too small').max(9_999_999, 'Amount too large'),
});
/**
 * Delete an additional cost line from a purchase order
 */
export const deleteAdditionalCostSchema = z.object({
    orderId: uuidSchema,
    costId: uuidSchema,
});
/**
 * Override the unit price on a purchase order line item (BLU-205)
 * null = clear override and revert to catalog cost
 */
export const overridePurchaseOrderItemPriceSchema = z.object({
    itemId: uuidSchema,
    orderId: uuidSchema,
    overriddenUnitPrice: z.number().positive().max(99999999.99).finite().nullable(),
});
/**
 * Undo / revert an order to its previous status (BLU-347)
 * Reverses stock movements where applicable.
 */
export const undoOrderStatusSchema = z.object({
    orderId: uuidSchema,
    locationId: uuidSchema,
});
/**
 * Mark a received order as paid (BLU-373 PR2)
 * Terminal status — locks exchange_rate forever via DB trigger.
 * Admin-only mutation. Idempotent (paid → paid is a no-op success).
 */
export const markOrderPaidSchema = z.object({
    orderId: uuidSchema,
    locationId: uuidSchema,
});
/**
 * Revert received items back to pending (BLU-344)
 * Decrements stock and resets processing status.
 * itemIds omitted = revert all processed items on the order.
 */
export const revertReceivedItemsSchema = z.object({
    orderId: uuidSchema,
    locationId: uuidSchema,
    itemIds: z.array(uuidSchema).optional(),
    notes: mediumString.optional(),
});
/**
 * Adjust unit_cost on a received purchase order item (BLU-373 PR3 — Tier 1)
 *
 * Allowed on partially_received and received POs. Admin + Coordinator
 * (Coordinator must have access to the PO's receive location).
 *
 * Rebases sku_inventory.weighted_average_cost on the remaining stock at the
 * PO's location, in org base currency. Past order_items.cogs_unit_cost is
 * intentionally untouched.
 */
export const adjustReceivedItemCostSchema = z.object({
    poItemId: uuidSchema,
    newUnitCost: z
        .number()
        .positive('Unit cost must be positive')
        .max(9_999_999.99, 'Unit cost too large')
        .finite(),
    reason: z
        .string()
        .trim()
        .min(5, 'Reason must be at least 5 characters')
        .max(500, 'Reason too long'),
});
/**
 * Result of adjustReceivedItemCost — mirrors the JSON returned by atomic_rebase_wac.
 *
 * `wacUnchanged: true` is returned when stock at the location is fully
 * consumed (or no inventory record exists). In that branch, only the PO line
 * cost is updated and no audit row of type 'cost_adjustment' is written.
 */
export const adjustReceivedItemCostResultSchema = z.object({
    success: z.boolean(),
    wacUnchanged: z.boolean().optional(),
    reason: z.string().optional(),
    oldUnitCost: z.number().nullable().optional(),
    newUnitCost: z.number().optional(),
    oldWac: z.number().nullable().optional(),
    newWac: z.number().nullable().optional(),
    remainingQty: z.number().int().optional(),
    deltaBase: z.number().optional(),
    costChange: z.number().optional(),
});
